/**
 * Keeps Rubik combined-listing product cards in sync with an active storefront filter.
 *
 * Shopify returns the representative product for a Rubik group. Rubik then exposes the
 * other group products inside its open shadow root. When an active filter matches one
 * of those option values, this script changes the card image, title and destination to
 * the matching group product. No product handles or colour names are hard-coded.
 */
(function () {
  'use strict';

  const productCache = new Map();
  const retryDelays = [0, 150, 400, 800, 1500, 3000, 5000];

  function normalize(value) {
    return String(value || '')
      .replace(/\(\s*\d+\s*\)\s*$/, '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ß/g, 'ss')
      .replace(/[^a-zA-Z0-9]+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function addValue(values, value) {
    const normalized = normalize(value);
    if (normalized && !values.includes(normalized)) values.push(normalized);
  }

  function activeFilterValues() {
    const values = [];

    document.querySelectorAll('.active-filter').forEach((filter) => {
      addValue(values, filter.textContent);
    });

    document.querySelectorAll('#facets input[name^="filter."]:checked').forEach((input) => {
      addValue(values, input.value);
      if (input.id) {
        const label = document.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        addValue(values, label?.textContent);
      }
    });

    new URLSearchParams(window.location.search).forEach((value, key) => {
      if (key.startsWith('filter.') && !key.includes('.price.')) addValue(values, value);
    });

    return values;
  }

  function rubikOptions(card) {
    const host = card.querySelector('rubik-combined-listings');
    const root = host?.shadowRoot;
    if (!root) return [];

    return [...root.querySelectorAll('a[data-option-value][href]')].map((link) => ({
      link,
      href: link.getAttribute('href'),
      value: normalize(link.dataset.optionValue || link.getAttribute('aria-label'))
    }));
  }

  function matchingOption(card, filterValues) {
    const options = rubikOptions(card);
    return options.find((option) => filterValues.includes(option.value));
  }

  function productEndpoint(href) {
    const url = new URL(href, window.location.origin);
    const match = url.pathname.match(/\/products\/([^/]+)/);
    return match ? `/products/${match[1]}.js` : null;
  }

  async function getProduct(href) {
    const endpoint = productEndpoint(href);
    if (!endpoint) return null;

    if (!productCache.has(endpoint)) {
      productCache.set(endpoint, fetch(endpoint, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
          return response.json();
        })
        .catch((error) => {
          productCache.delete(endpoint);
          console.warn('[Rubik filter card sync]', error);
          return null;
        }));
    }

    return productCache.get(endpoint);
  }

  function imageUrl(image) {
    if (typeof image === 'string') return image;
    return image?.src || image?.url || '';
  }

  function withWidth(source, width) {
    if (!source) return '';
    const url = new URL(source, window.location.origin);
    url.searchParams.set('width', width);
    return url.toString();
  }

  function updateImages(card, product) {
    const sources = (product.images || []).map(imageUrl).filter(Boolean);
    const featured = imageUrl(product.featured_image);
    if (featured && !sources.includes(featured)) sources.unshift(featured);
    if (!sources.length) return;

    const widths = [320, 460, 600, 700, 800, 900, 1200];
    card.querySelectorAll('.card__media img.card__main-image, .card__media img.card__hover-image').forEach((image, index) => {
      const source = sources[Math.min(index, sources.length - 1)] || sources[0];
      image.src = withWidth(source, 460);
      image.srcset = widths.map((width) => `${withWidth(source, width)} ${width}w`).join(', ');
      image.alt = product.title || image.alt;
      image.removeAttribute('data-src');
      image.removeAttribute('data-srcset');
    });
  }

  function updateDestinations(card, href, product) {
    const targetUrl = new URL(href, window.location.origin).pathname;

    card.querySelectorAll('a[href*="/products/"]').forEach((link) => {
      if (!link.closest('rubik-combined-listings')) {
        link.href = targetUrl;
        if (link.classList.contains('media')) link.setAttribute('aria-label', product.title || '');
      }
    });

    card.querySelectorAll('[data-product-url]').forEach((element) => {
      element.dataset.productUrl = targetUrl;
    });

    if (product.id) {
      card.querySelectorAll('[data-product-id]').forEach((element) => {
        element.dataset.productId = product.id;
      });
    }

    const title = card.querySelector('.card__title .card-link');
    if (title && product.title) title.textContent = product.title;
  }

  function markRubikOption(card, selectedLink) {
    rubikOptions(card).forEach(({ link }) => {
      const selected = link === selectedLink;
      link.classList.toggle('rcl__swatch--active', selected);
      if (selected) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });
  }

  async function syncCard(card, filterValues) {
    const option = matchingOption(card, filterValues);
    if (!option?.href) return;

    const targetPath = new URL(option.href, window.location.origin).pathname;
    if (card.dataset.rubikFilterProduct === targetPath) return;

    const product = await getProduct(option.href);
    if (!product || !card.isConnected) return;

    updateImages(card, product);
    updateDestinations(card, option.href, product);
    markRubikOption(card, option.link);
    card.dataset.rubikFilterProduct = targetPath;
  }

  function syncCards() {
    const filterValues = activeFilterValues();
    if (!filterValues.length) return;

    document.querySelectorAll('#filter-results product-card').forEach((card) => {
      syncCard(card, filterValues);
    });
  }

  function scheduleSync() {
    retryDelays.forEach((delay) => window.setTimeout(syncCards, delay));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleSync, { once: true });
  } else {
    scheduleSync();
  }

  document.addEventListener('on:facet-filters:updated', scheduleSync);
  window.addEventListener('pageshow', scheduleSync);
}());
